import React, { useEffect } from 'react';
import { useLocation, useNavigate, createSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useUser } from 'context/UserContext';
import { GetProductCodesBySoftwareID } from '../../../api/referencedata';

function ProtectedRoute({ permissions = [], allowedProducts, allowedProductsSoftwareID, element }) {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  // `user` is undefined until UserProvider has a token to build it from. Every route
  // reaching this component now sits below that provider's null-gate, so in practice the
  // user is already resolved — but an absent user must still mean "not resolved yet",
  // never "denied": with replace:true a premature denial clobbers history, and only
  // re-entering the app recovers from it.
  const resolved = Boolean(user);
  // Any-of semantics (unchanged): allowed if the user holds at least one required permission.
  // Empty/omitted permissions means no role-based restriction.
  const hasPermission = permissions.length === 0 || permissions.some((e) => user?.permissions?.[e]);

  // allowedProductsSoftwareID fetches the allowed product codes from the backend instead of
  // relying on a hardcoded array. Fail-closed on error: an unresolved/errored fetch means
  // no product codes are treated as allowed, never an open gate.
  const getAllowedProducts = useQuery({
    queryKey: ['GetProductCodesBySoftwareID', allowedProductsSoftwareID],
    queryFn: async () => await GetProductCodesBySoftwareID(allowedProductsSoftwareID)(),
    enabled: allowedProductsSoftwareID != null,
  });
  const productsQuerySettled =
    allowedProductsSoftwareID == null || getAllowedProducts.isSuccess || getAllowedProducts.isError;
  const effectiveAllowedProducts = allowedProductsSoftwareID != null
    ? getAllowedProducts.data ?? []
    : allowedProducts;

  // allowedProducts/allowedProductsSoftwareID omitted means no LOB restriction; otherwise
  // the signed-in user's own LOB (from their SSO claims) must include one of the allowed
  // product codes.
  const hasLobAccess =
    !effectiveAllowedProducts ||
    user?.LOB?.some((p) => effectiveAllowedProducts.includes(p.ProductCode?.toUpperCase()));
  const allowed = resolved && productsQuerySettled && hasPermission && hasLobAccess;

  // Redirect from an effect, NOT the render body. A render-phase navigate() is
  // discarded by React, which left a denied route rendering a blank page instead of
  // bouncing to the access-denied screen.
  useEffect(() => {
    if (resolved && productsQuerySettled && !allowed) {
      navigate(
        {
          pathname: '/Error/AccessDenied',
          search: `?${createSearchParams({ requested: location.pathname })}`,
        },
        { replace: true }
      );
    }
  }, [resolved, productsQuerySettled, allowed, location.pathname, navigate]);

  return allowed ? <>{element}</> : null;
}
export { ProtectedRoute };
